import { EventEmitter, Injectable } from "@angular/core";
import { CHApiResultState, CallState, LoadingState } from "@app/root-store/api-result.state";
import { Action, Store, select } from "@ngrx/store";
import { Observable, combineLatest, from, of } from "rxjs";
import { concatMap, filter, map, switchMap, take, tap } from "rxjs/operators";
import { CHRule, CHRuleInt } from "../model/rule";
import { CommandActions, ConditionActions, RuleActions } from "../store/actions";
import { CommandsSelectors, ConditionsSelectors, RuleSelectors } from "../store/selectors";

import { CustomerLocationActions } from "@app/customer-portal/store/actions";
import { ProjectState } from "@app/project/store/projects.state";
import { ProjectSelectors } from "@app/project/store/selectors";
import { onlineClareStatus } from "@app/shared/model/clare-status.model";
import { ClareHomeDbCollection } from "@app/shared/resources/resource-objectifier";
import { DataConverterService } from "@app/shared/services/converter/data-converter.service";
import { ProjectInfoKeys } from "@app/shared/services/equator-api/equator-projectinfo/equator-projectinfo.service";
import { CommandProviders } from "../model/commands/command";
import { SceneCommand } from "../model/commands/scene-command";
import { Condition } from "../model/conditions";
import { clearRuleState } from "../store/actions/ch-rules.actions";
import { CHRuleState } from "../store/rule.state";

@Injectable({
    providedIn: "root",
})
export class CHRulesService {
    constructor(private store: Store<CHRuleState | ProjectState>, private readonly dataConverter: DataConverterService) {}

    public conditionEmitter = new EventEmitter<any>();
    dispatchAction(action: Action) {
        this.store.dispatch(action);
    }

    isProjectOnline$(result) {
        return this.store.pipe(
            select(ProjectSelectors.getActiveProjectStatus),
            filter(status => status === onlineClareStatus),
            map(_ => result)
        );
    }

    selectRulesState$(): Observable<CHApiResultState<ClareHomeDbCollection<CHRuleInt>>> {
        return this.store.pipe(
            select(RuleSelectors.getChRules),
            filter(rulesState => !!rulesState?.uuid),
            switchMap(result => this.isProjectOnline$(result)),
            tap(rulesState => {
                if (rulesState.callState === LoadingState.init) {
                    return this.store.dispatch(RuleActions.loadSet.request({ uuid: rulesState.uuid }));
                }
            })
        );
    }

    getRule$() {
        return this.store.pipe(
            select(RuleSelectors.getCurrentRule),
            filter(ruleState => !!ruleState?.uuid),
            tap(ruleState => {
                if (ruleState.callState === LoadingState.init) {
                    return this.store.dispatch(RuleActions.loadSet.request({ uuid: ruleState.uuid }));
                }
            })
        );
    }

    getActivePanelCommands() {
        return this.store.pipe(
            select(CommandsSelectors.selectCommandsState),
            filter(commandState => !!commandState?.uuid),
            tap(commandState => {
                if (commandState.callState === LoadingState.init) {
                    this.store.dispatch(CommandActions.loadSet.request({ uuid: commandState.uuid }));
                }
            }),
            filter(commandState => commandState.callState === LoadingState.loaded)
        );
    }

    getCommandById$(commandId: string) {
        return this.getActivePanelCommands().pipe(
            switchMap(_ => this.store.pipe(select(CommandsSelectors.selectCommand(commandId)))),
            map(commandState => commandState.result)
        );
    }

    getConditionById$(conditionId: string) {
        return this.store.pipe(
            select(ConditionsSelectors.selectCondition(conditionId)),
            filter(conditionState => !!conditionState?.uuid),
            tap(conditionState => {
                if (conditionState.callState === LoadingState.init) {
                    return this.store.dispatch(ConditionActions.loadSet.request({ uuid: conditionState.uuid }));
                }
            }),
            map(conditionState => conditionState.result)
        );
    }

    getMultiConditionsByIdIfIdExists$(conditions: Condition[]) {
        return from(conditions).pipe(
            concatMap((condition: Condition) => {
                if (condition.conditionId) {
                    return this.store.pipe(select(ConditionsSelectors.selectCondition(condition.conditionId)), take(1));
                }
                return of({ result: condition });
            })
        );
    }

    getConditionsById$(conditions: Condition[]) {
        return from(conditions).pipe(
            concatMap((condition: Condition) =>
                this.store.pipe(select(ConditionsSelectors.selectCondition(condition.conditionId)), take(1))
            )
        );
    }

    selectRules$(): Observable<{ result: CHRuleInt[]; uuid: string; callState: CallState<ClareHomeDbCollection<CHRuleInt>> }> {
        return this.selectRulesState$().pipe(
            tap(result => {
                if (result.callState === LoadingState.init) {
                    return this.store.dispatch(RuleActions.loadSet.request({ uuid: result.uuid }));
                }
            }),
            map(state => ({
                ...state,
                result: Object?.values(state.result || {})?.sort((a, b) => (a.name || "").localeCompare(b.name || "")),
            })),
            switchMap(state => {
                if (state.callState === LoadingState.loaded) {
                    return this.getActivePanelCommands().pipe(
                        map(commandState => commandState.result),
                        map(commands => {
                            const rules = state.result;
                            rules.forEach(rule => {
                                const ruleCommand = commands[rule.commandId];
                                if (ruleCommand?.commandType === CommandProviders.SCENE) {
                                    rule.command = ruleCommand as SceneCommand;
                                    rule.type = rule.conditionType;
                                }
                            });
                            return state;
                        })
                    );
                }

                return of(state);
            })
        );
    }

    selectRuleStatusCounts$() {
        return this.store.pipe(select(RuleSelectors.getRuleStatusCounts));
    }

    createRule$(rule: CHRuleInt, callbackEmitter: EventEmitter<any>) {
        return this.store.pipe(
            select(ProjectSelectors.getUuid),
            tap(uuid => this.store.dispatch(RuleActions.createSet.request({ uuid, rule, callbackEmitter })))
        );
    }

    // /accounts/edit/:id/rules/add
    // /myaccount/locations/:uuid/rules/add
    selectDealerAndUIScenes$() {
        return combineLatest({
            scenesState: this.store.pipe(select(CommandsSelectors.selectDealerSceneCommandsState)),
            uiAndDealerScenes: this.store.pipe(select(RuleSelectors.getUIAndDealerScenes)),
        }).pipe(
            filter(({ scenesState }) => !!scenesState?.uuid),
            switchMap(result => this.isProjectOnline$(result)),
            tap(({ scenesState }) => {
                if (scenesState.callState === LoadingState.init) {
                    this.store.dispatch(CommandActions.loadSet.request({ uuid: scenesState.uuid }));
                    this.store.dispatch(RuleActions.loadSet.request({ uuid: scenesState.uuid }));
                }
            }),
            filter(({ uiAndDealerScenes }) => !!uiAndDealerScenes),
            map(({ uiAndDealerScenes }) => uiAndDealerScenes)
        );
    }

    selectSceneCommandLoadingState$() {
        return this.store.pipe(select(CommandsSelectors.loadingCommandState));
    }

    selectControllerTimezoneAndCoordinates$() {
        return this.store.pipe(
            select(RuleSelectors.getLocationDataState),
            filter(locationData => !!locationData && !!locationData.uuid),
            switchMap(locationData => this.isProjectOnline$(locationData)),
            tap(result => {
                if (result.callState === LoadingState.init) {
                    return this.store.dispatch(new CustomerLocationActions.GetLocationData({ uuid: result.uuid }));
                }
            }),
            filter(result => !!result.result),
            map(result => ({
                timezone: result.result[ProjectInfoKeys.timezone],
                coordinates: result.result[ProjectInfoKeys.coordinates],
            }))
        );
    }

    convertValue(conversionKey, original, service, input = false) {
        let _original = original;

        if (conversionKey) {
            if (input) {
                _original = this.dataConverter.getInputValue(original, conversionKey, service?.units || null, true);
            } else {
                _original = this.dataConverter.getOutputValue(original, conversionKey, service && service.units ? service.units : null);
            }
        }

        return _original;
    }

    getDataConverter(conversionKey, service) {
        return this.dataConverter.getConverter(conversionKey, service.units);
    }

    clearRulesState() {
        this.store.dispatch(clearRuleState());
    }

    clearCommandsState() {
        this.store.dispatch(CommandActions.clearCommandState());
    }

    clearConditionsState() {
        this.store.dispatch(ConditionActions.clearConditionState());
    }

    getCHRuleFromCHRuleInt(inputInterface: CHRuleInt): CHRule {
        return new CHRule(
            inputInterface.ruleId,
            inputInterface.name,
            inputInterface.commandId,
            inputInterface.commandType,
            inputInterface.conditionId,
            inputInterface.conditionType,
            inputInterface.active,
            inputInterface.properties,
            inputInterface.createdByMch,
            inputInterface.internal
        );
    }
    getRuleNote(isBasicPlan: boolean, category: string) {
        const exceptList = ["Send a Notification", "Security"];
        if (isBasicPlan && exceptList.includes(category)) {
            return `Feature not available on Basic plan`;
        }
        return null;
    }
}
